import type { DepositInitialAmount } from '../../flow/depositFlowConfig'
import { useSourceTokenUsdPrice } from '../../withdrawal/useSourceTokenUsdPrice'
import type { DepositSourceToken } from '../depositAssets'
import {
  isPositiveFinite,
  lockedTokenAmount,
  resolveFiatRate,
} from '../lockedTokenAmount'
import { useFiatExchangeRates } from './useFiatExchangeRates'

export function useLockedTokenAmount(
  initialAmount: DepositInitialAmount | undefined,
  token: DepositSourceToken | undefined,
  enabled: boolean,
) {
  const active = enabled && !!initialAmount && !!token
  const price = useSourceTokenUsdPrice(token, {
    enabled: active,
    refetchInterval: 30_000,
  })
  const needsRates =
    active && !price.isUnavailable && initialAmount.currency !== 'USD'
  const rates = useFiatExchangeRates(needsRates)
  const hasQueryError = price.isError || (needsRates && rates.isError)
  const pending = price.isPending || (needsRates && rates.isPending)
  const fetching = price.isFetching || (needsRates && rates.isFetching)
  const amount =
    active && !hasQueryError && !pending && price.data !== undefined
      ? lockedTokenAmount(
          initialAmount,
          price.data,
          needsRates ? rates.data : undefined,
          token.decimals,
        )
      : undefined
  const isError = active && (hasQueryError || (!pending && !amount))
  const invalidCurrency =
    needsRates &&
    rates.isSuccess &&
    resolveFiatRate(initialAmount, rates.data) === undefined
  const canRetry =
    isError &&
    !price.isUnavailable &&
    !invalidCurrency &&
    (hasQueryError || !isPositiveFinite(price.data))
  return {
    amount,
    canRetry,
    isError,
    isLoading: active && fetching && !amount,
    retry: () => {
      if (!active || !canRetry || fetching) {
        return
      }
      if (price.isError || !isPositiveFinite(price.data)) {
        void price.refetch()
      }
      if (needsRates) {
        void rates.refetch()
      }
    },
  }
}
